import { Box, Chip, IconButton, ListItemIcon, Menu, MenuItem, Typography } from "@mui/material"
import { ChevronLeftOutlined, MoreVertOutlined, VisibilityOutlined } from "@mui-symbols-material/w300"
import { Link, useNavigate } from "@tanstack/react-router"
import type { FC } from "react"
import React, { useState } from "react"

import DisplayIds from "@/components/common/DisplayIds/DisplayIds"
import type { Product } from "@/graphql/codegen/graphql"

interface ProductPreferencesHeaderProps {
  productDetails: Product
  companyId?: string
  customerCompanyName?: string | null
  customerDescription?: string | null
  customerCodes?: string[]
}

const ProductPreferencesHeader: FC<ProductPreferencesHeaderProps> = ({
  productDetails,
  companyId,
  customerCompanyName,
  customerDescription,
  customerCodes = [],
}) => {
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isMenuOpen = Boolean(anchorEl)

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleViewProductDetails = () => {
    handleMenuClose()
    navigate({
      to: "/app/product/$productId/edit/information",
      params: { productId: String(productDetails.productId) },
    })
  }

  return (
    <>
      <Box className='flex justify-between'>
        <Box className='flex items-center gap-4'>
          <Link
            to='/app/companies/$companyId/edit/products'
            params={{ companyId: companyId ?? productDetails?.company?.companyId }}
          >
            <IconButton aria-label='back to company' className='rounded bg-gray-200 text-primary'>
              <ChevronLeftOutlined />
            </IconButton>
          </Link>
          <Typography variant='h5' color='primary' fontWeight='normal'>
            {customerCompanyName ?? ""} Product Preferences
          </Typography>
        </Box>
      </Box>

      <Box className='mt-8 rounded-md border border-solid border-gray-400 p-6'>
        <Box className='flex items-center justify-between gap-3'>
          <Box className='flex items-center gap-3'>
            <Typography variant='overline' className='tracking-[1px] text-gray-700'>
              AKROCHEM DESCRIPTION
            </Typography>
          </Box>

          <IconButton
            color='primary'
            aria-label='edit product preference options'
            onClick={handleMenuOpen}
            aria-haspopup
            data-testid='product-preference-icon-btn'
          >
            <MoreVertOutlined />
          </IconButton>

          <Menu
            id='product-preferences-menu'
            data-testid='product-preferences-menu'
            anchorEl={anchorEl}
            open={isMenuOpen}
            onClose={handleMenuClose}
            MenuListProps={{
              "aria-labelledby": "product-edit-button",
            }}
          >
            <MenuItem onClick={handleViewProductDetails}>
              <ListItemIcon>
                <VisibilityOutlined />
              </ListItemIcon>
              View Product Details
            </MenuItem>
          </Menu>
        </Box>
        <Box className='flex items-center gap-3'>
          <Typography variant='h5' className='text-gray-800'>
            {productDetails?.companyProductDescription}
          </Typography>
          <DisplayIds
            entityName='Product ID'
            publicId={productDetails?.publicId}
            externalSystemId={productDetails?.externalSystemId}
          />
        </Box>

        <Box className='mt-4 flex flex-col gap-2'>
          <Typography variant='body2' className='text-gray-700'>
            Customer Description: <span className='font-medium'>{customerDescription || "-"}</span>
          </Typography>
          <Box className='flex items-center gap-2'>
            <Typography variant='body2' className='text-gray-700'>
              Customer Codes:
            </Typography>
            {customerCodes.length > 0 ? (
              customerCodes.map((code) => (
                <Chip key={code} size='small' label={code} className='rounded-[4px] bg-gray-300 text-xs' />
              ))
            ) : (
              <Typography variant='body2' className='text-gray-700'>
                -
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default ProductPreferencesHeader
